import styles from "./SuspendUserModal.module.css";
import PropTypes from "prop-types";
import { useState, useEffect, useRef } from "react";

import { Modal } from "@wwg/ui";

import Checkbox from "shared/Checkbox";
import { Select } from "shared/Select/Select";
import classNames from "classnames";
import Button from "shared/Button";
import emailTemplate from "../../Shared/EmailTemplate/emailTemplate";

import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
} from "@react-pdf/renderer";

/**
 *
 * Modal with a form to suspend a user.
 *
 */

export function SuspendUserModal({
  userInfo,
  isOpen,
  onConfirm,
  onClose,
  refetch,
}) {
  const [dreamStream, setDreamStream] = useState(false);
  const [communikate, setCommunikate] = useState(false);
  const [events, setEvents] = useState(false);
  const [digitalDelivery, setDigitalDelivery] = useState(false);
  const [suspensionLength, setSuspensionLength] = useState("");
  const [suspendedIndefinitely, setSuspendedIndefinitely] = useState(false);
  const [suspensionReason, setSuspensionReason] = useState("");
  const [template, setTemplate] = useState("");
  const [subject, setSubject] = useState("");
  const [email, setEmail] = useState("");
  const [reminder, setReminder] = useState("");
  const [isSuspended, setIsSuspended] = useState(false);
  const [templateUpdated, setTemplateUpdated] = useState(false);
  const [errors, setErrors] = useState({
    platforms: "",
    reason: "",
    template: "",
    subject: "",
    email: "",
    suspensionLength: "",
    reminder: "",
  });

  useEffect(() => {
    clearForm();
    if (isOpen && userInfo?.pendingSuspension) {
      userInfo.pendingSuspension.platforms.forEach((platform) => {
        if (platform.status === "suspended") {
          setIsSuspended(true);
        }

        if (platform.status === "pending" || platform.status === "suspended") {
          if (platform.name === "DreamStream") {
            setDreamStream(true);
          } else if (platform.name === "CommuniKate") {
            setCommunikate(true);
          } else if (platform.name === "Events") {
            setEvents(true);
          } else if (platform.name === "Membership/Digital Delivery") {
            setDigitalDelivery(true);
          }
        }
      });

      setSuspensionLength(userInfo.pendingSuspension.suspendedDays);
      setSuspendedIndefinitely(userInfo.pendingSuspension.suspendIndefinitely);
      setSuspensionReason(userInfo.pendingSuspension.reason);
      setTemplate(userInfo.pendingSuspension.template);
      setSubject(userInfo.pendingSuspension.subject);
      setEmail(userInfo.pendingSuspension.emailBody);
    }
  }, [userInfo, isOpen]);

  useEffect(() => {
    if (templateUpdated) {
      if (template !== "Choose Template") {
        const getTemplate = emailTemplate(template);
        setSubject(getTemplate.subject);
        setEmail(getTemplate.body);
      }
    }
  }, [template]);

  const handleSubmit = async () => {
    let errors = {
      platforms: "",
      suspensionLength: "",
      reason: "",
      template: "",
      subject: "",
      email: "",
      reminder: "",
    };

    if (!dreamStream && !communikate && !events && !digitalDelivery) {
      errors.platforms = "Please select an option.";
    }

    if (suspensionLength === 0 && !suspendedIndefinitely) {
      errors.suspensionLength = "Suspension length cannot be set to 0.";
    }

    if (suspensionReason === "")
      errors.reason = "Please enter a suspension reason.";

    if (template === "") {
      errors.template = "Please select a template.";
    }

    if (subject === "") errors.subject = "Please enter a subject.";
    if (email === "") errors.email = "Please enter a body.";

    if (reminder === 0) errors.reminder = "Reminder days cannot be set to 0.";

    let empty = true;
    for (let key in errors) {
      if (errors[key].length) empty = false;
    }
    if (empty) {
      const finalPlatforms = [];

      if (dreamStream) {
        finalPlatforms.push("DreamStream");
      }
      if (communikate) {
        finalPlatforms.push("CommuniKate");
      }
      if (events) {
        finalPlatforms.push("Events");
      }
      if (digitalDelivery) {
        finalPlatforms.push("Membership/Digital Delivery");
      }

      onConfirm({
        id: userInfo._id,
        platforms: finalPlatforms,
        suspendIndefinitely: suspendedIndefinitely,
        suspendedDays: suspensionLength,
        reason: suspensionReason,
        reminderDays: reminder,
        template: template,
        subject: subject,
        emailBody: email,
        approveSuspension: "true",
      });

      clearForm();
      await refetch();
      onClose();
    } else {
      setErrors({ ...errors });
    }
  };

  const getPlatforms = () => {
    let statusText = "";
    if (dreamStream) {
      statusText += "DreamStream";
    }
    if (communikate) {
      if (statusText !== "") {
        statusText += ", ";
      }
      statusText += "CommuniKate";
    }
    if (events) {
      if (statusText !== "") {
        statusText += ", ";
      }
      statusText += "Events";
    }
    if (digitalDelivery) {
      if (statusText !== "") {
        statusText += ", ";
      }
      statusText += "Membership/Digital Delivery";
    }
    return statusText !== "" ? statusText : "None";
  };

  const formattedDateTime = new Date().toLocaleString("en-US", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const pdfStyles = StyleSheet.create({
    page: {
      display: "flex",
      flexDirection: "row",
    },
    section: {
      marginBottom: "20px",
      padding: "20px",
      display: "flex",
      flexDirection: "column",
    },
    title: {
      textDecoration: "underline",
      marginBottom: "20px",
      marginTop: "20px",
      fontWeight: 100,
    },
    text: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
    },
    row: {
      marginBottom: "10px",
    },
    listText: {
      whiteSpace: "pre-line",
      lineBreak: "anywhere",
      marginBottom: "20px",
    },
    listItem: {
      marginBottom: "10px",
    },
  });

  const PDF = () => (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.section}>
          <Text style={pdfStyles.title}>SUSPENSION INFORMATION</Text>
          <Text style={pdfStyles.text}>Suspended From: {getPlatforms()}</Text>
          <Text style={pdfStyles.text}>
            Suspended for: {suspensionLength} day(s)
          </Text>
          <Text style={pdfStyles.text}>
            Suspended Indefinitely: {suspendedIndefinitely ? "Yes" : "No"}
          </Text>
          <Text style={pdfStyles.text}>
            Suspension Reason: {suspensionReason}
          </Text>
          <Text style={pdfStyles.title}>EMAIL</Text>
          <Text style={pdfStyles.text}>
            To:{" "}
            {userInfo &&
              userInfo.complaintAgainst &&
              userInfo.complaintAgainst.email &&
              userInfo.complaintAgainst.email}
          </Text>
          <Text style={pdfStyles.text}>Sent: {formattedDateTime}</Text>
          <Text style={pdfStyles.text}>Template: {template}</Text>
          <Text style={pdfStyles.text}>Subject: {subject}</Text>
          <Text style={pdfStyles.text}>Body: {email}</Text>
        </View>
      </Page>
    </Document>
  );

  const clearForm = () => {
    setDreamStream(false);
    setCommunikate(false);
    setEvents(false);
    setDigitalDelivery(false);
    setSuspensionLength("");
    setSuspendedIndefinitely(false);
    setSuspensionReason("");
    setTemplate("");
    setSubject("");
    setEmail("");
    setReminder("");
    setErrors({
      platforms: "",
      reason: "",
      template: "",
      subject: "",
      email: "",
      suspensionLength: "",
      reminder: "",
    });
    setTemplateUpdated(false);
    setIsSuspended(false);
  };

  return (
    <Modal
      visible={isOpen}
      onClick={onClose}
      children={
        <div className={styles.container}>
          <div className={styles.header}>
            Suspend User{" "}
            {isSuspended && <span className={styles.errors}>*Suspended*</span>}
          </div>
          <div className={styles.hr} />
          <div className={styles.title}>
            What platforms is user being suspended from?
          </div>
          <Checkbox
            checked={dreamStream}
            onChange={(e) => {
              setDreamStream(!dreamStream);
            }}
            className={styles.checkbox}
            visible={!isSuspended}
          />
          {(!isSuspended || (isSuspended && dreamStream)) && (
            <div className={styles.checkboxLabel}>DreamStream</div>
          )}
          <br />
          <Checkbox
            checked={communikate}
            onChange={(e) => {
              setCommunikate(!communikate);
            }}
            className={styles.checkbox}
            visible={!isSuspended}
          />
          {(!isSuspended || (isSuspended && communikate)) && (
            <div className={styles.checkboxLabel}>CommuniKate</div>
          )}
          <br />
          <Checkbox
            checked={events}
            onChange={(e) => {
              setEvents(!events);
            }}
            className={styles.checkbox}
            visible={!isSuspended}
          />
          {(!isSuspended || (isSuspended && events)) && (
            <div className={styles.checkboxLabel}>Events</div>
          )}
          <br />
          <Checkbox
            checked={digitalDelivery}
            onChange={(e) => {
              setDigitalDelivery(!digitalDelivery);
            }}
            className={styles.checkbox}
            visible={!isSuspended}
          />
          {(!isSuspended || (isSuspended && digitalDelivery)) && (
            <div className={styles.checkboxLabel}>
              Membership/Digital Delivery
            </div>
          )}
          <br />
          <span className={styles.errors}>{errors.platforms}</span>
          <div className={styles.title}>
            How long will user be suspended for?
          </div>
          {((isSuspended && suspensionLength > 0) || !isSuspended) && (
            <>
              <div className={styles.durationSelect}>
                <Select
                  id="suspensionLength"
                  options={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
                  placeholder={
                    suspensionLength !== "" ? `${suspensionLength}` : "Select"
                  }
                  onChange={setSuspensionLength}
                  disabled={isSuspended}
                />
              </div>
              <span>days</span>
            </>
          )}
          {!isSuspended && <span className={styles.or}>OR</span>}

          <Checkbox
            id="suspendedIndefinitely"
            checked={suspendedIndefinitely}
            onChange={() => {
              setSuspendedIndefinitely(!suspendedIndefinitely);
              setSuspensionLength(0);
            }}
            className={styles.checkbox}
            visible={!isSuspended}
          />
          {((isSuspended && suspendedIndefinitely) || !isSuspended) && (
            <div className={styles.checkboxLabel}>
              User will be suspended indefinitely
            </div>
          )}

          <span className={styles.errors}>{errors.suspensionLength}</span>
          <br />
          <div className={styles.title}>
            Why are you suspending this user?{" "}
            <span className={styles.keepItShort}>Keep it short.</span>
          </div>
          <input
            placeholder="Suspension Reason"
            id="suspensionReason"
            value={suspensionReason}
            onChange={(e) => {
              setSuspensionReason(e.target.value);
              setErrors({ ...errors, reason: "" });
            }}
            className={classNames(styles.input, styles.reasonInput)}
            disabled={isSuspended}
          />
          <span className={styles.errors}>{errors.reason}</span>
          <div className={styles.title}>Send an Email</div>
          <div className={styles.templateSelect}>
            <Select
              options={[
                "Caution Letter",
                "Review Letter",
                "Probation Letter",
                "Disciplinary Letter",
                "Disciplinary Letter - Physical Threat",
                "Withdrawl Letter",
                "Admonish Letter",
                "Reinstated Letter",
                "Case Completion Letter",
              ]}
              placeholder={template}
              onChange={(value) => {
                setTemplateUpdated(true);
                setTemplate(value);
              }}
              disabled={isSuspended}
            />
          </div>
          <span className={styles.errors}>{errors.template}</span>
          <input
            placeholder="Subject Line"
            value={subject}
            onChange={(e) => {
              setSubject(e.target.value);
              setErrors({ ...errors, subject: "" });
            }}
            className={classNames(styles.input)}
            disabled={isSuspended}
          />
          <span className={styles.errors}>{errors.subject}</span>
          <textarea
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors({ ...errors, email: "" });
            }}
            placeholder="Add text here…"
            className={classNames(styles.textarea)}
            value={email}
            disabled={isSuspended}
          ></textarea>
          <span className={styles.errors}>{errors.email}</span>
          {!isSuspended && (
            <>
              <div className={styles.title}>Set Reminder</div>
              <div>
                <span className={styles.remindMeIn}>Remind me in </span>
                <span className={styles.durationSelect}>
                  <Select
                    id="reminder"
                    options={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13]}
                    placeholder={reminder !== "" ? `${reminder}` : "Select"}
                    onChange={setReminder}
                  />
                </span>
                <span> days.</span>
                <br></br>
                <span className={styles.errors}>{errors.reminder}</span>
              </div>
            </>
          )}
          <div className={styles.buttonContainer}>
            <PDFDownloadLink
              document={<PDF />}
              fileName={`incident_${userInfo._id}`}
            >
              <Button className={styles.printPDFButton}>Print PDF</Button>
            </PDFDownloadLink>

            {!isSuspended && (
              <button className={styles.button} onClick={() => handleSubmit()}>
                Confirm
              </button>
            )}
          </div>
        </div>
      }
    />
  );
}

SuspendUserModal.propTypes = {
  userInfo: PropTypes.object,
  isOpen: PropTypes.bool,
  onConfirm: PropTypes.func,
  onClose: PropTypes.func,
};
